import { MessageBarType } from '@fluentui/react';
import {
  Button,
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  Input,
  Text,
  TableColumnDefinition,
  TableColumnId,
  SortDirection,
  createTableColumn
} from '@fluentui/react-components';
import { DeleteRegular, EditRegular, WarningRegular } from '@fluentui/react-icons';
import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { AvailableMedicalService } from '../../../models/Entities/MedicalServices/AvailableMedicalService';
import { PriceDetails } from '../../../models/Entities/PriceList/PriceDetail';
import { MessageComponent } from '../../../commons/component/MessageComponent';

// functions
import * as Functions from '../../../functions/common';
import * as ComponentFunctions from './PriceListComponentFunctions';
import './PricesList.css';

////i18Next
import { useTranslation } from 'react-i18next';

export const PricesListComponent = (props: ComponentFunctions.Props) => {
  const [sortedItems, setSortedItems] = useState<PriceDetails[]>([]);
  const [sortState, setSortState] = useState<
    | { sortColumn: TableColumnId; sortDirection: SortDirection }
    | undefined
  >();

  const [t] = useTranslation();

  useEffect(() => {
    if (!props.PriceList && !props.IsGettingPriceListById) {
      props.GetPriceListById(props.PriceListId);
    }
  }, [props.PriceListId]);

  useEffect(() => {
    if (props.MedicalServices.length === 0) {
      props.GetAllMedicalServices(1, 1000, undefined);
    }
  }, [props.MedicalServices]);

  useEffect(() => {
    if (!props.PriceMassiveEditionIsActive) {
      props.PriceList?.prices.forEach((price: PriceDetails) => {
        if (price.isChanged) {
          props.SavePrice(price);
        }
      });
    }
  }, [props.PriceMassiveEditionIsActive]);

  useEffect(() => {
    if (props.MedicalServices && props.PriceList) {
      const enriched = props.PriceList.prices
        .map((price: PriceDetails) =>
          price.itemReferredId
            ? {
                ...price,
                Description:
                  props.MedicalServices.find(
                    (service: AvailableMedicalService) =>
                      service.id === price.itemReferredId
                  )?.serviceNameReference || 'Desconocido'
              }
            : price
        )
        .sort(Functions.DynamicSort('Description'))
        .filter((price: any) =>
          props.ServiceFilterCriteria
            ? price.Description.toLowerCase().includes(
                props.ServiceFilterCriteria.toLowerCase()
              )
            : true
        );
      setSortedItems(enriched);
    }
  }, [props.PriceList?.prices, props.MedicalServices, props.ServiceFilterCriteria, props.PriceList]);

  const makePriceCell =
    (
      fieldKey: keyof PriceDetails,
      onChange: (item: PriceDetails, newValue: number) => void
    ) =>
    (item: PriceDetails) =>
      !props.PriceMassiveEditionIsActive ? (
        <span className="pricesGrid-priceCell">
          {item[fieldKey] as string | number} <small>{item.currency}</small>
        </span>
      ) : (
        <Input
          type="number"
          defaultValue={(item[fieldKey] as number | undefined)?.toString()}
          contentAfter={<small>{item.currency}</small>}
          onChange={(_ev, data) => onChange(item, Number(data.value))}
          onFocus={(e) => e.target.select()}
          className={item.isChanged ? 'TextFieldValueModified' : ''}
          style={{ width: '100%' }}
        />
      );

  const columns = useMemo((): TableColumnDefinition<PriceDetails>[] => {
    const cols: TableColumnDefinition<PriceDetails>[] = [];

    cols.push(
      createTableColumn<PriceDetails>({
        columnId: 'description',
        compare: (a, b) =>
          (a.Description || '').localeCompare(b.Description || ''),
        renderHeaderCell: () => t('priceList.medicalPracticeColumn.practice'),
        renderCell: (item) => (
          <Text style={{ textTransform: 'uppercase' }}>
            {item.Description}
            {!item.isEnabled && (
              <WarningRegular
                aria-label={t(
                  'priceList.medicalPracticeColumn.disabledService'
                )}
                className="pricesGrid-disabledIcon"
              />
            )}
          </Text>
        )
      })
    );

    if (props.showPrice)
      cols.push(
        createTableColumn<PriceDetails>({
          columnId: 'price',
          compare: (a, b) => (a.price ?? 0) - (b.price ?? 0),
          renderHeaderCell: () => t('priceList.priceColumn.price'),
          renderCell: makePriceCell('price', (item, val) =>
            props.ChangePrice({ ...item, price: val })
          )
        })
      );

    if (props.showRemotePrice)
      cols.push(
        createTableColumn<PriceDetails>({
          columnId: 'remotePrice',
          compare: (a, b) => (a.remotePrice ?? 0) - (b.remotePrice ?? 0),
          renderHeaderCell: () => t('priceList.priceColumn.remotePrice'),
          renderCell: makePriceCell('remotePrice', (item, val) =>
            props.ChangeRemotePrice({ ...item, remotePrice: val })
          )
        })
      );

    if (props.showPreScreaningPrice)
      cols.push(
        createTableColumn<PriceDetails>({
          columnId: 'preScreeningPrice',
          compare: (a, b) =>
            (a.preScreeningPrice ?? 0) - (b.preScreeningPrice ?? 0),
          renderHeaderCell: () =>
            t('priceList.priceColumn.preScreeningPrice'),
          renderCell: makePriceCell('preScreeningPrice', (item, val) =>
            props.ChangePreScreaningPrice({ ...item, preScreeningPrice: val })
          )
        })
      );

    if (props.showRandomPrice)
      cols.push(
        createTableColumn<PriceDetails>({
          columnId: 'randomPrice',
          compare: (a, b) => (a.randomPrice ?? 0) - (b.randomPrice ?? 0),
          renderHeaderCell: () => t('priceList.priceColumn.randomPrice'),
          renderCell: makePriceCell('randomPrice', (item, val) =>
            props.ChangeRandomPrice({ ...item, randomPrice: val })
          )
        })
      );

    if (props.showFailPrice)
      cols.push(
        createTableColumn<PriceDetails>({
          columnId: 'failPrice',
          compare: (a, b) => (a.failPrice ?? 0) - (b.failPrice ?? 0),
          renderHeaderCell: () => t('priceList.priceColumn.failurePrice'),
          renderCell: makePriceCell('failPrice', (item, val) =>
            props.ChangeFailPrice({ ...item, failPrice: val })
          )
        })
      );

    if (props.showAdditionalPaymentPrice)
      cols.push(
        createTableColumn<PriceDetails>({
          columnId: 'additionalPaymentPrice',
          compare: (a, b) =>
            (a.additionalPaymentPrice ?? 0) - (b.additionalPaymentPrice ?? 0),
          renderHeaderCell: () => t('priceList.priceColumn.copay'),
          renderCell: makePriceCell('additionalPaymentPrice', (item, val) =>
            props.ChangeAdditionalPaymentPrice({
              ...item,
              additionalPaymentPrice: val
            })
          )
        })
      );

    cols.push(
      createTableColumn<PriceDetails>({
        columnId: 'options',
        renderHeaderCell: () => '',
        renderCell: (item) => (
          <span className="pricesGrid-rowActions">
            <Button
              appearance="subtle"
              size="small"
              icon={<EditRegular />}
              aria-label={t('common.actions.edit')}
              title={t('common.actions.edit')}
              onClick={() => props.onEditPrice?.(item)}
            />
            <Button
              appearance="subtle"
              size="small"
              icon={<DeleteRegular />}
              aria-label={t('common.actions.delete')}
              title={t('common.actions.delete')}
              onClick={() => props.DeletePrice?.(item)}
            />
          </span>
        )
      })
    );

    return cols;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.PriceMassiveEditionIsActive,
    props.showPrice,
    props.showRemotePrice,
    props.showPreScreaningPrice,
    props.showRandomPrice,
    props.showFailPrice,
    props.showAdditionalPaymentPrice,
    t
  ]);

  const displayItems = useMemo(() => {
    if (!sortState) return sortedItems;
    const col = columns.find((c) => c.columnId === sortState.sortColumn);
    if (!col?.compare) return sortedItems;
    const sorted = [...sortedItems].sort(col.compare);
    return sortState.sortDirection === 'descending'
      ? sorted.reverse()
      : sorted;
  }, [sortedItems, sortState, columns]);

  return (
    <>
      {(props.FailOnGettingAllPriceList ||
        props.FailOnGettingPriceListById ||
        props.FailOnGettingAllMedicalServices) && (
        <MessageComponent
          message={t('priceList.failMessage.errorMessage')}
          type={MessageBarType.error}
          itemList={
            (props.PriceListOperationError &&
              props.PriceListOperationError.Errors) ||
            (props.MedicalServiceOperationError &&
              props.MedicalServiceOperationError.Errors) ||
            []
          }
          subMessage={
            props.PriceListOperationError?.ErrorMessage ||
            props.MedicalServiceOperationError?.ErrorMessage ||
            ''
          }
        />
      )}
      {(props.FailOnUpdatingPrice || props.FailOnDeletingPrice) && (
        <MessageComponent
          message={t('priceList.failMessage.errorMessage')}
          type={MessageBarType.error}
          itemList={
            (props.PriceOperationError &&
              props.PriceOperationError.Errors) ||
            []
          }
          subMessage={props.PriceOperationError?.ErrorMessage || ''}
        />
      )}
      {props.ServiceFilterCriteria && (
        <MessageComponent
          message={t('priceList.filterMessage.appliedFilter')}
          type={MessageBarType.info}
          itemList={[]}
          subMessage={
            t('priceList.filterMessage.searchCriteria') +
            ' "' +
            props.ServiceFilterCriteria +
            '"'
          }
        />
      )}
      <div className="pricesGrid-wrapper">
        <DataGrid
          className="pricesGrid"
          items={displayItems}
          columns={columns}
          sortable
          size="small"
          focusMode="composite"
          getRowId={(item) => item.id?.toString() ?? ''}
          onSortChange={(_ev, nextSortState) =>
            setSortState(
              nextSortState.sortColumn
                ? {
                    sortColumn: nextSortState.sortColumn,
                    sortDirection: nextSortState.sortDirection
                  }
                : undefined
            )
          }
        >
          <DataGridHeader>
            <DataGridRow>
              {({ renderHeaderCell }) => (
                <DataGridHeaderCell>
                  {renderHeaderCell()}
                </DataGridHeaderCell>
              )}
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody<PriceDetails>>
            {({ item, rowId }) => (
              <DataGridRow<PriceDetails> key={rowId}>
                {({ renderCell }) => (
                  <DataGridCell>{renderCell(item)}</DataGridCell>
                )}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
      </div>
      {props.PriceList?.prices.length === 0 && (
        <div className="pricesGrid-empty">
          {t('medicalSpecialties.priceList.noPrices')}
        </div>
      )}
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(PricesListComponent as any);


